import React, { FC } from 'react'
import EFSDataContext from './EFSDataContext'

// Type for the Input Provider props
type EFSInputProviderProps = {
  /** Whether to parse the data elements as JSON objects
   *
   * This only affects data given as data-attributes */
  parseJSON?: boolean
  /** The id of the element which holds the EFS data */
  efsDataID?: string
  /** The name of a function which will return the EFS data */
  setEFSDataCallback?: string
}

/**
 * The Provider component for the EFS Input
 *
 * This grabs all of the data attributes of from either the tag with the given ID
 * or from the output of a provided global callback.
 *
 * The data is parsed and then provided into a context which can be
 * consumed by the useEFSData hook
 */
const EFSInputProvider: FC<EFSInputProviderProps> = ({
  parseJSON = true,
  efsDataID = 'efs-data',
  setEFSDataCallback = 'setEFSDataCallback',
  children,
}) => {
  // imports the EFS input data tag
  const efsDataTag = document.getElementById(efsDataID)
  let efsData: { [name: string]: string | undefined } = {}

  if (efsDataTag) {
    efsData = { ...efsDataTag.dataset }

    if (parseJSON) {
      Object.keys(efsData).forEach((key) => {
        try {
          const unparsedData = efsData[key]
          const parsedData = JSON.parse(unparsedData!)
          efsData[key] = parsedData
        } catch (err) {
          // Ignore the error - not great!
        }
      })
    }
  }

  // Parse the global function if it exists
  // window as any is ugly, but it allows globals to work -- though yes, globals are evil
  if ((window as any)[setEFSDataCallback]) {
    efsData = { ...efsData, ...(window as any)[setEFSDataCallback]() }
  }

  return (
    <EFSDataContext.Provider value={efsData}>
      {children}
    </EFSDataContext.Provider>
  )
}

export default EFSInputProvider
